import React, { useEffect, useState } from "react";
import { Badge } from "react-bootstrap";
import {
  CalendarDays,
  Clock,
  Play,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import StudentTopicRow from "./StudentTopicRow";
import { MaterialChips, RecordingLinks } from "./StudentSyllabusLinks";
import VideoPlayerModal from "../../Videos/VideoPlayerModal";
import apiClient from "../../../utils/apiClient";

/**
 * StudentSyllabusDayCard
 *
 * Read-only day card for the student syllabus timeline.
 *
 * Props:
 *   - session        : Object  { dayNumber, title, plannedDate, status, topics }
 *   - isCurrent      : Boolean (is this the active/current day?)
 *   - isExpanded     : Boolean
 *   - onToggleExpand : Function
 *   - batchTiming    : Object  { startTime, endTime } (optional)
 */
const StudentSyllabusDayCard = ({
  session,
  batchId,
  isCurrent,
  isExpanded,
  onToggleExpand,
  batchTiming,
}) => {
  const [linkedMaterials, setLinkedMaterials] = useState({}); // { topicIndex: [material, ...] }, -1 = the whole day
  const [playing, setPlaying] = useState(null); // the recording open in the player

  // Fetch linked materials when expanded
  useEffect(() => {
    if (!isExpanded || !batchId || !session?.dayNumber) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await apiClient.get(
          `/materials/batch/${batchId}/by-syllabus-day/${session.dayNumber}`
        );
        if (!alive) return;
        const byTopic = {};
        (data.data || []).forEach((m) => {
          const idx = m.linkedTopicIndex ?? -1;
          if (!byTopic[idx]) byTopic[idx] = [];
          byTopic[idx].push(m);
        });
        setLinkedMaterials(byTopic);
      } catch {
        if (alive) setLinkedMaterials({});
      }
    })();
    return () => { alive = false; };
  }, [isExpanded, batchId, session?.dayNumber]);
  if (!session) return null;

  const statusConfig = {
    scheduled: { label: "Upcoming", icon: CalendarDays },
    in_progress: { label: "In Progress", icon: Play },
    completed: { label: "Completed", icon: CheckCircle2 },
  };

  const current = statusConfig[session.status] || statusConfig.scheduled;
  const StatusIcon = current.icon;

  const completedTopics =
    session.topics?.filter((t) => t.status === "completed").length || 0;
  const totalTopics = session.topics?.length || 0;
  const dayRecordings = session.recordings || [];
  const dayMaterials = linkedMaterials[-1] || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  };

  const formatTime = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  const iconClass = `ss-day-icon ss-icon-${session.status}`;
  const cardClass = [
    "ss-day-card",
    isCurrent ? "ss-day-in-progress" : "",
    session.status === "completed" ? "ss-day-completed" : "",
    isExpanded ? "ss-day-expanded" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cardClass}>
      {/* ── Clickable header ── */}
      <div
        className="ss-day-header"
        onClick={onToggleExpand}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onToggleExpand()}
      >
        <div className="ss-day-header-left">
          <span className="ss-day-chevron">
            {isExpanded ? (
              <ChevronDown size={18} />
            ) : (
              <ChevronRight size={18} />
            )}
          </span>
          <div className={iconClass}>
            <StatusIcon size={18} />
          </div>
          <div className="ss-day-header-text">
            <span className="ss-day-title">
              {session.label || `Day ${session.dayNumber}`} — {session.title}
            </span>
            {/* Batch timing display */}
            {batchTiming?.startTime && batchTiming?.endTime && (
              <span className="ss-day-timing">
                ⏰ {formatTime(batchTiming.startTime)} – {formatTime(batchTiming.endTime)}
              </span>
            )}
            {/* Date + collapsed summary */}
            {!isExpanded ? (
              <span className="ss-day-summary">
                {formatDate(session.plannedDate)}
                {totalTopics > 0 &&
                  ` · ${completedTopics}/${totalTopics} topics`}
              </span>
            ) : (
              session.plannedDate && (
                <span className="ss-day-date">
                  <Clock size={11} />
                  {formatDate(session.plannedDate)}
                </span>
              )
            )}
          </div>
        </div>

        <div className="ss-day-header-right">
          {isCurrent && session.status !== "completed" && (
            <span className="ss-day-current-label">Current</span>
          )}
          <Badge
            className={`ss-day-status-badge ss-status-${session.status}`}
          >
            <StatusIcon size={13} />
            {current.label}
          </Badge>
        </div>
      </div>

      {/* ── Expanded body ── */}
      {isExpanded && (
        <div className="ss-day-body">
          {/* The whole day's recordings and materials */}
          {(dayRecordings.length > 0 || dayMaterials.length > 0) && (
            <div className="ss-day-extras">
              <span className="ss-topics-label">For the whole day</span>
              <RecordingLinks recordings={dayRecordings} onPlay={setPlaying} />
              <MaterialChips materials={dayMaterials} />
            </div>
          )}

          <div className="ss-topics-header">
            <span className="ss-topics-label">Topics</span>
            <span className="ss-topics-count">
              {completedTopics}/{totalTopics} completed
            </span>
          </div>

          <div className="ss-topics-list">
            {session.topics?.map((topic, index) => (
              <StudentTopicRow
                key={index}
                index={index}
                topic={topic}
                linkedMaterials={linkedMaterials[index] || []}
                onPlayRecording={setPlaying}
              />
            ))}
            {(!session.topics || session.topics.length === 0) && (
              <div className="ss-no-topics">No topics for this day.</div>
            )}
          </div>
        </div>
      )}

      {playing && (
        <VideoPlayerModal
          show
          role="student"
          batchId={batchId}
          video={{ _id: playing.videoId, title: playing.title, recordedAt: playing.recordedAt, batch: batchId }}
          onClose={() => setPlaying(null)}
        />
      )}
    </div>
  );
};

export default StudentSyllabusDayCard;
